import React, { useState } from 'react';
import {
  Box,
  styled,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import { Lock } from 'lucide-react';
import { getSeasonDisplay, SEASONS, AIRDROP_SEASON } from 'constants';
import FooterBig from 'assets/season2/landing-page-footer-big.svg';
import FooterSmall from 'assets/season2/landing-page-footer-small.svg';
import Cursor from 'assets/season2/cursor.svg';
import LandingBackground from 'assets/season2/bg-landing.png';

// Password protection shown automatically when the current season is outside its date range.
// Displays dynamically based on whether the season hasn't started yet or has already ended.

/********************  Styled Components  ********************/
const Container = styled(Box)(({ theme }) => ({
  height: '100vh',
  backgroundColor: theme.palette.background.filter,
  cursor: `url('${Cursor}'), auto`,
  position: 'relative',
}));

const TopSection = styled('div')(({ theme }) => ({
  padding: theme.spacing(8),
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
  height: 620,
  background: `linear-gradient(90deg, #171717 0%, #142D20 25%, rgba(14, 79, 47, 0.70) 50%, rgba(0, 171, 88, 0.00) 75%), url(${LandingBackground}) 60% 100%`,
  backgroundRepeat: 'no-repeat',
  backgroundPositionX: '100%',

  '@media (min-width: 1440px)': {
    background: `linear-gradient(90deg, #171717 0%, #142D20 50%, rgba(14, 79, 47, 0.70) 70%, rgba(0, 171, 88, 0.00) 90%), url(${LandingBackground}) 100% 100%`,
  },
  '@media (max-width: 1280px)': {
    height: 440,
    paddingTop: theme.spacing(6),
  },
  '@media (max-width: 905px)': {
    background: `linear-gradient(90deg, #142D20 0%, rgba(14, 79, 47, 0.70) 25%, rgba(0, 171, 88, 0.00) 50%), url(${LandingBackground}) 100% 100%`,
  },
  '@media (max-width: 768px)': {
    height: 540,
  },
  '@media (max-width: 600px)': {
    padding: theme.spacing(4),
    paddingTop: theme.spacing(12),
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  '@media (max-width: 480px)': {
    height: 460,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(10),
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
}));

const PrimaryHeading = styled(Typography)(({ theme }) => ({
  fontSize: 70,
  fontWeight: 600,
  width: '100%',

  '@media (max-width: 1280px)': {
    fontSize: 60,
  },
  '@media (max-width: 905px)': {
    fontSize: 55,
  },
  '@media (max-width: 768px)': {
    fontSize: 45,
    marginTop: theme.spacing(6),
  },
  '@media (max-width: 600px)': {
    textAlign: 'center',
    fontSize: 38,
    marginTop: theme.spacing(4),
  },
  '@media (max-width: 480px)': {
    fontSize: 30,
  },
}));

const SecondaryHeading = styled(Typography)(({ theme }) => ({
  fontSize: 56,
  fontWeight: 500,
  marginBottom: theme.spacing(3),
  width: '100%',

  '@media (max-width: 1280px)': {
    fontSize: 50,
  },
  '@media (max-width: 905px)': {
    fontSize: 42,
  },
  '@media (max-width: 600px)': {
    fontSize: 35,
    textAlign: 'center',
  },
  '@media (max-width: 480px)': {
    fontSize: 25,
  },
}));

const BottomSection = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 412,
  margin: '0 auto',
  backgroundColor: theme.palette.background.filter,
  paddingBottom: 100,
  borderTop: '1px solid #525F6B',
  backgroundImage: `url(${FooterBig})`,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'top',

  '@media (max-width: 1280px)': {
    minHeight: 280,
  },
  '@media (max-width: 768px)': {
    backgroundImage: `url(${FooterSmall})`,
    minHeight: 880,
  },
}));

const AdminLoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: 350,
  width: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  position: 'absolute',
  bottom: 100,
  right: 20,
  zIndex: 10,
}));

/********************  Main Component  ********************/
const PasswordProtection = ({ onAuthenticated }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Check credentials - admin / Energi1234
    if (username === 'admin' && password === 'Energi1234') {
      // Store authentication in sessionStorage (temporary, cleared when browser closes)
      sessionStorage.setItem('temp_auth', 'true');
      onAuthenticated();
    } else {
      setError('Invalid credentials');
    }
  };

  return (
    <Container>
      <TopSection>
        {Date.now() < SEASONS[AIRDROP_SEASON].start ? (
          <>
            {AIRDROP_SEASON > 1 && (
              <SecondaryHeading
                style={{ textAlign: 'center', marginTop: '1.5em', marginBottom: 0 }}
              >
                The GMI {getSeasonDisplay(AIRDROP_SEASON - 1)} Airdrop has concluded! 🎆
              </SecondaryHeading>
            )}
            <PrimaryHeading style={{ textAlign: 'center' }}>
              {getSeasonDisplay(AIRDROP_SEASON)} will be launching soon, stay tuned! 👀
            </PrimaryHeading>
          </>
        ) : (
          <>
            <SecondaryHeading style={{ textAlign: 'center', marginTop: '1.5em', marginBottom: 0 }}>
              The GMI {getSeasonDisplay(AIRDROP_SEASON)} Airdrop has concluded! 🎆
            </SecondaryHeading>
            <PrimaryHeading style={{ textAlign: 'center' }}>
              Stay tuned for the next season! 👀
            </PrimaryHeading>
          </>
        )}
      </TopSection>
      <BottomSection />

      {/* Admin Access - Hidden by default */}
      <Box sx={{ position: 'absolute', bottom: 20, right: 20, zIndex: 10 }}>
        <IconButton
          onClick={() => setShowAdminLogin(!showAdminLogin)}
          sx={{
            color: 'rgba(255, 255, 255, 0.2)',
            '&:hover': {
              color: 'rgba(255, 255, 255, 0.4)',
            },
          }}
        >
          <Lock size={20} />
        </IconButton>
      </Box>

      {/* Admin Login Form - Collapsible */}
      <Collapse in={showAdminLogin}>
        <AdminLoginPaper elevation={3}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center' }}
          >
            Admin Access
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              size="small"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size="small"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} size="small">
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              type="submit"
              size="small"
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(76, 175, 80, 1)',
                },
              }}
            >
              Access
            </Button>
          </form>
        </AdminLoginPaper>
      </Collapse>
    </Container>
  );
};

export default PasswordProtection;
